<script setup>
import { computed } from 'vue'
import PlayerCrest from '@/components/PlayerCrest.vue'

/**
 * Player name, linked to their public profile.
 *
 * The name is unique in the database, so it doubles as the username in the profile URL.
 * Everything that will decorate the name later (nationality flag, titles, ...) belongs here: the
 * pages that show a player drop this component in, and they all get it at once.
 */
const props = defineProps({
  /** Player name as the API returns it. */
  name: { type: String, default: null },
  /** { shape, colors } — the crest the name wears, as the server sends it (see PlayerFlairLib). */
  crest: { type: Object, default: null },
  /** Whether the crest leads the name: off where the name sits inside a sentence. */
  showCrest: { type: Boolean, default: true },
})

const to = computed(() => ({ name: 'player', params: { username: props.name } }))
</script>

<template>
  <RouterLink v-if="name" :to="to" class="user-name user-name--link">
    <PlayerCrest v-if="showCrest" :crest="crest" />
    {{ name }}
  </RouterLink>
  <span v-else class="user-name">–</span>
</template>

<style scoped>
/* The name keeps the colour of the context (table, card) and only shows itself as a link on
   hover, so a classification stays readable instead of turning into a wall of blue.

   It never breaks in two lines either: the crest in front of it and the wins chip after it would be
   torn apart as soon as a column gets tight. */
.user-name {
  white-space: nowrap;
  color: inherit;
  text-decoration: none;
}

.user-name--link:hover {
  color: rgb(var(--v-theme-primary));
  text-decoration: underline;
}
</style>
